import * as Sentry from "@sentry/react";
import { store } from "../app/store";
import { updateToken } from "../reducers/authReducer";
import type { Platform } from "../reducers/cryptoReducer";

const ALLARIA_URL = import.meta.env.VITE_BASE_URL_ALLARIA;
const IOS_ALLARIA_URL = import.meta.env.VITE_BASE_URL_ALLARIA_IOS;

// Where the user is sent when the crypto onboarding cannot run. Kept as a
// constant instead of an env var so a missing deploy variable can never turn the
// redirect into a navigation to "undefined".
export const ALLARIA_DIGITAL_URL = "https://allariadigital.com.ar/";

// Terms the user has to accept to open the Allaria Digital account. Read straight
// from `import.meta.env` instead of the `isDev` helper in app/utils: that module
// reaches back into this one through connectors, and the circular import would
// leave the flag undefined while this constant is being initialised — silently
// pointing production at the dev PDF.
export const CRYPTO_TERMS_URL =
  import.meta.env.VITE_ENVIRONMENT === "dev"
    ? "https://assets.allaria.dev/digital/terminos_y_condiciones_ob.pdf"
    : "https://assets.allaria.cloud/digital/terminos_y_condiciones_ob.pdf";

// Where the Allaria app is served from while it runs on a developer's machine.
// Only for running both projects locally: no deployed target's frame-ancestors
// lets localhost frame us, so the deployed onboarding can't be tested from a
// local app. The dev build is the only one that accepts them; staging and prod
// resolve this to an empty list.
const LOCAL_ALLARIA_URLS =
  import.meta.env.VITE_ENVIRONMENT === "dev"
    ? ["http://localhost:3000", "http://127.0.0.1:3000"]
    : [];

// Origins allowed to embed the crypto onboarding and to exchange postMessages
// with it. Web and Android use the Allaria web origin, iOS the Capacitor scheme.
export const allariaAllowedOrigins = [
  ALLARIA_URL,
  IOS_ALLARIA_URL,
  ...LOCAL_ALLARIA_URLS,
].filter((origin): origin is string => !!origin);

// How long to wait for the parent to answer DIGITAL_ONBOARDING_READY with the
// token before giving up. The handshake is a single postMessage round trip, so
// this is only a safety net — keep it generous, since redirecting a legitimate
// user mid-handshake is worse than waiting.
export const TOKEN_HANDSHAKE_TIMEOUT_MS = 10_000;

// Build-time sanity check: without these the handshake can never happen, so the
// token never arrives and every user ends up redirected away.
export const getMissingAllariaEnvVars = () =>
  [
    !ALLARIA_URL && "VITE_BASE_URL_ALLARIA",
    !IOS_ALLARIA_URL && "VITE_BASE_URL_ALLARIA_IOS",
  ].filter((name): name is string => !!name);

// The platform the app declared, straight from the URL. Read here as well as in
// the store because the handshake starts before React — and therefore before
// anything has dispatched setPlatform.
export const getPlatformFromUrl = (): Platform => {
  const platform = new URLSearchParams(window.location.search).get("platform");

  return platform === "ios" || platform === "android" ? platform : "web";
};

export const getIsMobileLayoutFromUrl = (): boolean | null => {
  const mobile = new URLSearchParams(window.location.search).get("mobile");

  return mobile === "true" ? true : mobile === "false" ? false : null;
};

// The origin every postMessage to the parent is addressed to. Never "*": the
// messages travel with the parent's own page, and widening the target would leak
// them to whoever framed us.
//
// Falls back to the URL while the store still has no platform, so a message sent
// during the early handshake is not addressed to the web origin by default and
// silently dropped by the iOS WebView.
export const getParentTargetOrigin = () => {
  // Whoever actually framed us, when the browser exposes it and we already trust
  // it. The platform only distinguishes the two configured hosts, so on dev — where
  // the app may be running on localhost — it would address the message to
  // app.allaria.dev and the parent would never receive it.
  const parentOrigin = getParentOrigin();
  if (parentOrigin && allariaAllowedOrigins.includes(parentOrigin)) {
    return parentOrigin;
  }

  return (store.getState().crypto.platform ?? getPlatformFromUrl()) === "ios"
    ? IOS_ALLARIA_URL
    : ALLARIA_URL;
};

// Bare notifications: the app only needs to know the event happened.
type ParentNotification =
  | "DIGITAL_ONBOARDING_READY"
  | "DIGITAL_ONBOARDING_BACK"
  | "DIGITAL_ONBOARDING_CLOSE"
  | "DIGITAL_ONBOARDING_COMPLETED";

// Everything the onboarding can say to the app. OPEN_EXTERNAL and RESIZE carry data.
type ParentMessage =
  | { type: ParentNotification }
  | { type: "DIGITAL_ONBOARDING_OPEN_EXTERNAL"; url: string }
  | { type: "DIGITAL_ONBOARDING_RESIZE"; height: number };

// Returns whether the message could be addressed, so callers that have a
// fallback know the app was never reached.
const post = (message: ParentMessage) => {
  const origin = getParentTargetOrigin();
  if (!origin) return false;

  window.parent.postMessage(message, origin);
  return true;
};

// Kept returning void: callers pass it straight as an event handler, and only
// openExternalInApp has anything to do with whether the app was reachable.
export const postToParent = (type: ParentNotification) => {
  post({ type });
};

// Asks the app to open a URL outside the WebView, through its own openExternal.
//
// `target="_blank"` cannot do this from inside the app: on iOS, WKWebView routes
// it to a WKUIDelegate that Capacitor does not implement and gets back nil, and
// Android WebView drops it unless the host enables setSupportMultipleWindows. In
// both cases the tap does nothing at all — no error, no navigation. Android has
// no built-in PDF viewer either, so even navigating this frame would only trade
// the dead link for a blank page and a lost form.
export const openExternalInApp = (url: string) =>
  post({ type: "DIGITAL_ONBOARDING_OPEN_EXTERNAL", url });

// Subscribes to the token the Allaria app pushes. The origin check is the only
// thing standing between the session token and any page that framed us, so it
// stays here, in one place, rather than being repeated per caller.
export const subscribeToParentToken = (onToken: (token: string) => void) => {
  if (!allariaAllowedOrigins.length) return () => {};

  const handleMessage = (event: MessageEvent) => {
    if (!allariaAllowedOrigins.includes(event.origin)) return;

    if (event.data?.type === "DIGITAL_ONBOARDING_INIT" && event.data.token) {
      onToken(event.data.token);
    }
  };

  window.addEventListener("message", handleMessage);

  return () => window.removeEventListener("message", handleMessage);
};

// Everything below runs before React: see startEarlyTokenHandshake.
let pendingParentToken: string | null = null;
let stopEarlyHandshake: (() => void) | null = null;

/**
 * Announces the onboarding to the Allaria app as soon as the page starts, without
 * waiting for React.
 *
 * The app gives up after TOKEN_HANDSHAKE_TIMEOUT_MS and shows "Algo salió mal".
 * Doing this from useCryptoBridge means the whole bundle has to be downloaded,
 * parsed and executed, the store rehydrated and the template fetched before the
 * first byte reaches the app — on a cold load that is slower than the timeout, so
 * the modal fails on an onboarding that is working perfectly. Announcing
 * readiness only needs the URL and `window.parent`, both available here.
 *
 * The token the app answers with is held instead of dispatched: redux-persist has
 * not rehydrated yet, and its REHYDRATE replaces the auth slice wholesale, so the
 * fresh token would be overwritten by the persisted one. useCryptoBridge picks it
 * up once the store is ready.
 */
export const startEarlyTokenHandshake = () => {
  // Not embedded means no app to talk to; the guard sends these users away.
  if (window.parent === window || !allariaAllowedOrigins.length) return;

  stopEarlyHandshake = subscribeToParentToken((token) => {
    pendingParentToken = token;
  });

  postToParent("DIGITAL_ONBOARDING_READY");
};

/**
 * Hands the channel over to useCryptoBridge once the store can be written to:
 * returns whatever the app already answered and retires the early listener, so
 * from then on a single subscriber writes straight to the store.
 */
export const takeOverEarlyHandshake = () => {
  stopEarlyHandshake?.();
  stopEarlyHandshake = null;

  const token = pendingParentToken;
  pendingParentToken = null;

  return token;
};

// Asks the app for a token again and resolves with the one it sends back, after
// storing it. Used to recover from a 401 mid-flow: the token injected at the
// start can expire before the user finishes the liveness, which is the slowest
// part of the onboarding.
export const requestFreshToken = () =>
  new Promise<string | null>((resolve) => {
    // Nothing to ask on the remote QR steps: they run top-level on the phone and
    // take their token from Firestore.
    if (window.parent === window) {
      resolve(null);
      return;
    }

    let settled = false;
    const cleanups: Array<() => void> = [];

    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      cleanups.forEach((cleanup) => cleanup());

      if (token) store.dispatch(updateToken(token));
      resolve(token);
    };

    const timeout = setTimeout(() => finish(null), TOKEN_HANDSHAKE_TIMEOUT_MS);
    cleanups.push(subscribeToParentToken(finish), () => clearTimeout(timeout));

    postToParent("DIGITAL_ONBOARDING_READY");
  });

export type CryptoBlockReason =
  | "NOT_EMBEDDED"
  | "UNTRUSTED_PARENT"
  | "MISSING_ACCOUNT_PARAMS"
  | "MISSING_TOKEN";

// The draft the identity step validates against. The Allaria app creates it
// before opening the onboarding and passes it as `uuid`, the same param the QR
// links carry, so it lands in the store as the draftUUID.
export const getCryptoDraftIdFromUrl = () =>
  new URLSearchParams(window.location.search).get("uuid") ?? "";

// Native webviews are never treated as untrusted parents: the origin a Capacitor
// WKWebView reports is not confirmed on a device yet (it may be
// `capacitor://localhost` instead of the configured host) and blocking on a wrong
// guess would lock out the iOS app.
const NATIVE_WEBVIEW_SCHEMES = ["capacitor:", "ionic:", "file:"];

// Not implemented in Firefox, where it is undefined at runtime.
export const getParentOrigin = () =>
  window.location.ancestorOrigins?.[0] ?? null;

// The crypto onboarding is meant to run inside an iframe opened by the Allaria
// app, which injects the session token via postMessage. Everything it needs to
// work has to be there: the embedding context, the account params in the URL and
// the token. This covers the checks that can be resolved synchronously on mount;
// the token arrives asynchronously and is handled by useCryptoGuard.
export const getCryptoBlockReason = ({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string;
}): CryptoBlockReason | null => {
  if (window.parent === window) return "NOT_EMBEDDED";

  // Only comparable when the browser exposes the parent origin; a missing value
  // is not treated as untrusted, since the postMessage `event.origin` check is
  // what actually guards the token.
  const parentOrigin = getParentOrigin();
  if (
    parentOrigin &&
    !allariaAllowedOrigins.includes(parentOrigin) &&
    !NATIVE_WEBVIEW_SCHEMES.some((scheme) => parentOrigin.startsWith(scheme))
  ) {
    return "UNTRUSTED_PARENT";
  }

  if (!accountId || !accountName) return "MISSING_ACCOUNT_PARAMS";

  return null;
};

// Records who is embedding us and which platform the app declared.
//
// Sentry is not initialised when VITE_ENVIRONMENT === "dev" (see
// sentry/instrument.ts), so on the dev deploy the console is the only channel that
// works — and dev is exactly where the real iOS/Android parent origins have to be
// captured. Read it with Safari Web Inspector (iOS) or chrome://inspect (Android).
// setContext makes it ride along on every event once Sentry is on (staging/prod).
let hasReportedEmbedContext = false;

export const reportEmbedContext = () => {
  // Once per page load. The template can settle in more than one pass, so the
  // effect that calls this is not a reliable "once" on its own, and a diagnostic
  // line repeated N times reads as N different embeds.
  if (hasReportedEmbedContext) return;
  hasReportedEmbedContext = true;

  const context = {
    parentOrigin: getParentOrigin(),
    embedded: window.parent !== window,
    // Raw param on purpose: a missing or malformed value is what we want to see,
    // not the "web" fallback it gets normalised to.
    platformParam: new URLSearchParams(window.location.search).get("platform"),
    allowedOrigins: allariaAllowedOrigins,
  };

  Sentry.setContext("embed", context);
  // Serialised so the values survive remote webview logging, which flattens
  // objects to "Object".
  console.info(`[crypto-onboarding] embed context ${JSON.stringify(context)}`);
};

// Sends the user to Allaria Digital. `replace` keeps the unusable onboarding URL
// out of the history so going back does not return to it.
//
// Note this navigates the current frame: when the onboarding was opened as a
// plain URL (the main case) that is the tab, but when it is embedded the parent
// page stays put and only the iframe navigates — a cross-origin frame cannot
// reliably navigate its top.
export const redirectToAllariaDigital = async (reason: CryptoBlockReason) => {
  // Logged as well as reported: on dev, where Sentry is off, this is the only way
  // to know why the onboarding sent the user away.
  console.warn(
    `[crypto-onboarding] unavailable (${reason}) — redirecting to ${ALLARIA_DIGITAL_URL}`,
    { parentOrigin: getParentOrigin() },
  );

  Sentry.captureMessage(`Crypto onboarding unavailable: ${reason}`, {
    level: "warning",
    tags: { security: "crypto_invalid_context" },
    extra: { reason, parentOrigin: getParentOrigin() },
  });

  // The navigation would cancel the in-flight Sentry request.
  await Sentry.flush(2000).catch(() => {});

  window.location.replace(ALLARIA_DIGITAL_URL);
};

// ARIA's role, not Matter's class names, which its build may change.
const MODAL = '[role="dialog"]';
// 1rem above and below the modal.
const MODAL_FRAME_MARGIN = 32;

// Only in the app's desktop modal (mobile=false): full screen, the page fills
// the frame and there is nothing to report.
export const startReportingModalHeight = (isMobileLayout: boolean | null) => {
  if (window.parent === window || isMobileLayout !== false) {
    return () => {};
  }

  const app = document.getElementById("root");
  if (!app) return () => {};

  const html = document.documentElement;
  html.dataset.digitalModal = "";

  // Not the body: other stylesheets give it the frame's height as a minimum.
  // Modals render in a portal outside #root and are centered in the frame, so
  // an open one also counts, plus a margin, or it would scroll inside (the
  // modal itself drops its 85vh cap with Matter's disableMaxHeight).
  let lastHeight = 0;
  const measure = () => {
    let height = app.getBoundingClientRect().height;
    document.querySelectorAll<HTMLElement>(MODAL).forEach((modal) => {
      observer.observe(modal);
      // One exactly as tall as the frame is sized by it (the mobile sheet,
      // 100vh), not by its content: growing the frame would grow it too, with
      // no end. A taller one is content-sized and the frame must fit it.
      if (Math.abs(modal.offsetHeight - window.innerHeight) <= 1) return;
      height = Math.max(height, modal.offsetHeight + MODAL_FRAME_MARGIN);
    });
    height = Math.ceil(height);
    if (height === lastHeight) return;
    lastHeight = height;
    post({ type: "DIGITAL_ONBOARDING_RESIZE", height });
  };
  const observer = new ResizeObserver(measure);
  observer.observe(app);
  // Modals opening and closing: Radix mounts each portal straight in the body.
  const portals = new MutationObserver(measure);
  portals.observe(document.body, { childList: true });

  return () => {
    observer.disconnect();
    portals.disconnect();
    delete html.dataset.digitalModal;
  };
};
